import React, { useState, useEffect } from 'react';
import { Award, Trophy, Star, Clock, Moon, Calendar, Zap, Diamond, Bird, Flame, Crown } from 'lucide-react';
import certificateAPI from '../services/certificateAPI';
import { toast } from 'react-hot-toast';

const Prizes = () => {
  const [prizes, setPrizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState('');
  const [userPrizes, setUserPrizes] = useState([]);
  const [showUserPrizes, setShowUserPrizes] = useState(false);

  const prizeTypes = {
    'first_completion': {
      name: 'Explorer Badge',
      description: 'First hunt completion',
      icon: Trophy,
      rarity: 'common',
      color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    },
    'speed_demon': {
      name: 'Speed Demon',
      description: 'Completed hunt in under 1 hour',
      icon: Zap,
      rarity: 'rare',
      color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
    },
    'perfect_score': {
      name: 'Perfectionist',
      description: 'Solved all puzzles without hints',
      icon: Diamond,
      rarity: 'epic',
      color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
    },
    'night_owl': {
      name: 'Night Owl',
      description: 'Completed hunt after 8 PM',
      icon: Moon,
      rarity: 'uncommon',
      color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
    },
    'weekend_warrior': {
      name: 'Weekend Warrior',
      description: 'Completed hunt on weekend',
      icon: Calendar,
      rarity: 'common',
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
    },
    'early_bird': {
      name: 'Early Bird',
      description: 'Completed hunt before 10 AM',
      icon: Bird,
      rarity: 'uncommon',
      color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
    },
    'streak_master': {
      name: 'Streak Master',
      description: 'Completed 3 hunts in a row',
      icon: Flame,
      rarity: 'epic',
      color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    },
    'explorer_legend': {
      name: 'Explorer Legend',
      description: 'Completed 10 hunts total',
      icon: Crown,
      rarity: 'legendary',
      color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
    }
  };

  useEffect(() => {
    loadPrizes();
  }, []);

  const loadPrizes = async () => {
    try {
      setLoading(true);
      const response = await certificateAPI.getAllPrizes();
      if (response.success) {
        setPrizes(response.data);
      } else {
        throw new Error(response.error || 'Failed to load prizes');
      }
    } catch (error) {
      console.error('Error loading prizes:', error);
      toast.error('Failed to load prizes');
      // Fallback to empty array if API fails
      setPrizes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewUserPrizes = async (email) => {
    try {
      setSelectedUser(email);
      const response = await certificateAPI.getUserPrizes(email);
      setUserPrizes(response.data || []);
      setShowUserPrizes(true);
    } catch (error) {
      console.error('Error loading user prizes:', error);
      toast.error('Failed to load user prizes');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'common':
        return 'text-green-600 dark:text-green-400';
      case 'uncommon':
        return 'text-blue-600 dark:text-blue-400';
      case 'rare':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'epic':
        return 'text-purple-600 dark:text-purple-400';
      case 'legendary':
        return 'text-yellow-600 dark:text-yellow-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="header-gradient rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">🎁 Virtual Prizes</h1>
            <p className="mt-2 text-lg">
              Track and manage virtual prizes awarded to users
            </p>
            <div className="mt-4 flex items-center space-x-6">
              <div className="text-sm text-gray-600 dark:text-gray-300">
                <span className="font-semibold">{Object.keys(prizeTypes).length}</span> Prize Types Available
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                <span className="font-semibold">{prizes.length}</span> Recent Awards
              </div>
            </div>
          </div>
          <button
            onClick={loadPrizes}
            className="btn-secondary-gradient flex items-center space-x-2"
          >
            <Award className="h-5 w-5" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Prize Types Overview */}
      <div className="email-card">
        <div className="email-card-header">
          <h2 className="text-xl font-semibold">🏆 Available Prize Types</h2>
        </div>
        <div className="email-card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(prizeTypes).map(([key, prize]) => {
            const IconComponent = prize.icon;
            return (
              <div
                key={key}
                className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <IconComponent className={`h-8 w-8 ${getRarityColor(prize.rarity)}`} />
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">{prize.name}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{prize.description}</p>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${prize.color}`}>
                    {prize.rarity}
                  </span>
                </div>
              </div>
            );
          })}
          </div>
        </div>
      </div>

      {/* Recent Prizes */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Recent Prize Awards
          </h2>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {prizes.map((prize) => {
            const prizeInfo = prizeTypes[prize.prize_type || prize.prizeType];
            if (!prizeInfo) {
              console.warn('Unknown prize type:', prize.prize_type || prize.prizeType);
              return null;
            }
            const IconComponent = prizeInfo.icon;
            return (
              <div key={prize.id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <IconComponent className={`h-10 w-10 ${getRarityColor(prizeInfo.rarity)}`} />
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        {prizeInfo.name}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Awarded to {prize.user_name || prize.userName}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-500">
                        {prize.hunt_name || prize.huntName}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${prizeInfo.color}`}>
                      {prizeInfo.rarity}
                    </span>
                    <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                      {formatDate(prize.awarded_at || prize.awardedAt)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* User Prize Lookup */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Lookup User Prizes
        </h2>
        <div className="flex space-x-4">
          <input
            type="email"
            placeholder="Enter user email"
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          />
          <button
            onClick={() => handleViewUserPrizes(selectedUser)}
            disabled={!selectedUser}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            View Prizes
          </button>
        </div>
      </div>

      {/* User Prizes Modal */}
      {showUserPrizes && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Prizes for {selectedUser}
                </h3>
                <button
                  onClick={() => setShowUserPrizes(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                {userPrizes.length > 0 ? (
                  userPrizes.map((prize, index) => {
                    const prizeInfo = prizeTypes[prize.prize_type || prize.prizeType] || {
                      name: prize.name || 'Unknown Prize',
                      description: prize.description || 'Virtual prize',
                      icon: Award,
                      rarity: 'common',
                      color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                    };
                    const IconComponent = prizeInfo.icon;
                    return (
                      <div key={index} className="flex items-center space-x-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <IconComponent className={`h-8 w-8 ${getRarityColor(prizeInfo.rarity)}`} />
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 dark:text-white">{prizeInfo.name}</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{prizeInfo.description}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-500">
                            {prize.hunt_name || prize.huntName} - {formatDate(prize.awarded_at || prize.awardedAt)}
                          </p>
                        </div>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${prizeInfo.color}`}>
                          {prizeInfo.rarity}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    No prizes found for this user.
                  </p>
                )}
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowUserPrizes(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Prizes;
